import { createRef, useEffect, useState } from 'react'
import { TextArea } from '@progress/kendo-react-inputs'
import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadFileStatus,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload'
import { FaDownload, FaFileImage, FaTrash } from 'react-icons/fa'

import { getEnquirySurvey, updateEnquirySurvey } from '../../../data/enquiryData'
import {
  deleteDocument,
  downloadDocument,
  getDocument,
  getDocumentObjectUrl,
} from '../../../data/fileData'
import { fileDocumentTypes } from '../../../data/interfaces'
import { DateFormatter } from '../../../data/helper'
import { useToast } from '../../../contexts/ToastContext'

const apiURL = process.env.REACT_APP_API_URL

const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp']

interface EnquirySurveyProps {
  enquiryId: number
  lockEdit?: boolean
}

// A single survey photo. The file endpoint needs the auth header, so the image
// is fetched as a blob and shown from an object URL rather than a plain src.
const SurveyPhoto = ({
  doc,
  lockEdit,
  onDelete,
}: {
  doc: fileDocumentTypes
  lockEdit?: boolean
  onDelete: (doc: fileDocumentTypes) => void
}) => {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let url: string | null = null
    let cancelled = false

    getDocumentObjectUrl(doc.documentId)
      .then((objectUrl) => {
        if (cancelled) {
          window.URL.revokeObjectURL(objectUrl)
          return
        }
        url = objectUrl
        setSrc(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (url) window.URL.revokeObjectURL(url)
    }
  }, [doc.documentId])

  const iconButton = {
    background: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    borderRadius: '4px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    color: '#1e2a4e',
  }

  return (
    <div
      style={{
        position: 'relative',
        border: '1px solid #d8e5ed',
        borderRadius: '6px',
        overflow: 'hidden',
        background: '#f4f7f9',
      }}
    >
      <button
        type='button'
        onClick={() => getDocument(doc.documentId, doc.fileName)}
        title='Open full size'
        style={{
          display: 'block',
          width: '100%',
          aspectRatio: '4 / 3',
          padding: 0,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
        }}
      >
        {src && !failed ? (
          <img
            src={src}
            alt={doc.fileName}
            onError={() => setFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1e2a4e',
            }}
          >
            {failed ? <FaFileImage style={{ fontSize: '24pt' }} /> : <span style={{ fontSize: '9pt' }}>Loading…</span>}
          </div>
        )}
      </button>

      <div style={{ position: 'absolute', top: '6px', right: '6px', display: 'flex', gap: '4px' }}>
        <button
          type='button'
          title='Download'
          onClick={() => downloadDocument(doc.documentId, doc.fileName)}
          style={iconButton}
        >
          <FaDownload />
        </button>
        {!lockEdit && (
          <button type='button' title='Delete' onClick={() => onDelete(doc)} style={{ ...iconButton, color: '#b3261e' }}>
            <FaTrash />
          </button>
        )}
      </div>

      <div
        title={doc.fileName}
        style={{
          padding: '6px 8px',
          fontSize: '9pt',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          background: '#fff',
        }}
      >
        {doc.fileName}
      </div>
    </div>
  )
}

const EnquirySurvey = ({ enquiryId, lockEdit }: EnquirySurveyProps) => {
  const { showToast } = useToast()
  const dateFormatter = new DateFormatter()
  const token = localStorage.getItem('authToken')
  const uploadRef = createRef<Upload>()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notes, setNotes] = useState('')
  const [savedNotes, setSavedNotes] = useState('')
  const [updatedDate, setUpdatedDate] = useState<string | null>(null)
  const [updatedByName, setUpdatedByName] = useState<string | null>(null)
  const [documents, setDocuments] = useState<fileDocumentTypes[]>([])
  const [files, setFiles] = useState<UploadFileInfo[]>([])

  const applySurvey = (survey: Awaited<ReturnType<typeof getEnquirySurvey>>, includeNotes: boolean) => {
    if (includeNotes) {
      setNotes(survey.surveyNotes ?? '')
      setSavedNotes(survey.surveyNotes ?? '')
    }
    setUpdatedDate(survey.updatedDate)
    setUpdatedByName(survey.updatedByName)
    setDocuments(survey.documents)
  }

  const fetchSurvey = async (includeNotes: boolean) => {
    try {
      const survey = await getEnquirySurvey(enquiryId)
      applySurvey(survey, includeNotes)
    } catch (err) {
      showToast('Failed to load survey notes', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchSurvey(true)
  }, [enquiryId])

  const handleSave = async () => {
    setSaving(true)
    try {
      const survey = await updateEnquirySurvey(enquiryId, notes)
      applySurvey(survey, true)
      showToast('Survey notes saved', 'success')
    } catch (err) {
      showToast('Failed to save survey notes', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleUploadStatusChange = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState)

    const uploaded = event.affectedFiles.some((file) => file.status === UploadFileStatus.Uploaded)
    const failed = event.affectedFiles.some((file) => file.status === UploadFileStatus.UploadFailed)

    if (uploaded) {
      showToast('Survey photos uploaded', 'success')
      // Leave the notes alone so unsaved typing isn't lost when a photo lands.
      fetchSurvey(false)
    } else if (failed) {
      showToast('Failed to upload survey photo', 'error')
    }
  }

  const handleDelete = async (doc: fileDocumentTypes) => {
    if (!window.confirm(`Delete ${doc.fileName}?`)) return

    try {
      await deleteDocument(doc.documentId)
      setDocuments((prev) => prev.filter((d) => d.documentId !== doc.documentId))
      showToast('Survey photo deleted', 'success')
    } catch (err) {
      showToast('Failed to delete survey photo', 'error')
    }
  }

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading survey notes…</div>
  }

  const isDirty = notes !== savedNotes

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      <label className='k-label' style={{ fontSize: '10pt', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
        Survey notes
      </label>
      <TextArea
        value={notes}
        onChange={(e) => setNotes(e.value)}
        rows={10}
        disabled={lockEdit}
        placeholder='Record findings, measurements and observations from the survey...'
        style={{ width: '100%', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        {!lockEdit && (
          <button
            type='button'
            onClick={handleSave}
            disabled={saving || !isDirty}
            className='form__edit__button edit'
            style={{ margin: 0 }}
          >
            {saving ? 'Saving…' : 'Save survey notes'}
          </button>
        )}
        {updatedDate && (
          <span style={{ fontSize: '9pt', color: '#666' }}>
            Last updated {dateFormatter.format(updatedDate)}
            {updatedByName ? ` by ${updatedByName}` : ''}
          </span>
        )}
        {isDirty && !lockEdit && <span style={{ fontSize: '9pt', color: '#856404' }}>Unsaved changes</span>}
      </div>

      <div style={{ height: '1.5rem' }} />

      <label className='k-label' style={{ fontSize: '10pt', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
        Survey photos
      </label>
      <p style={{ margin: '0 0 0.75rem', fontSize: '9pt', color: '#666' }}>
        Photos added here also appear in the enquiry's Files section, marked as survey files.
      </p>

      {!lockEdit && (
        <>
          <ExternalDropZone style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }} uploadRef={uploadRef} />
          <div style={{ height: '12px' }} />
          <Upload
            ref={uploadRef}
            batch={true}
            multiple={true}
            files={files}
            withCredentials={false}
            restrictions={{ allowedExtensions }}
            saveHeaders={{ Authorization: `Bearer ${token}` }}
            saveUrl={token ? `https://${apiURL}/Files/Enquiry/${enquiryId}/Survey` : ''}
            onStatusChange={handleUploadStatusChange}
          />
          <div style={{ height: '1rem' }} />
        </>
      )}

      {documents.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px',
          }}
        >
          {documents.map((doc) => (
            <SurveyPhoto key={doc.documentId} doc={doc} lockEdit={lockEdit} onDelete={handleDelete} />
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: '1rem',
            backgroundColor: 'rgb(216 229 237)',
            borderRadius: '0.5rem',
          }}
        >
          <p style={{ margin: 0 }}>No survey photos yet</p>
        </div>
      )}
    </div>
  )
}

export default EnquirySurvey
